/**
 * VO2Max Report Validation Rules
 * Admin authorization required if any of these conditions are met
 */

export const validateVO2MaxReport = (formData) => {
  const violations = [];

  // Rule 1: Check for missing Age, Gender, Patient ID, Report ID
  const requiredPatientFields = [
    { key: 'patient_age', label: 'Age' },
    { key: 'patient_gender', label: 'Gender' },
    { key: 'patient_id', label: 'Patient ID' },
  ];

  for (const field of requiredPatientFields) {
    if (!formData[field.key] || formData[field.key] === '' || formData[field.key] === null) {
      violations.push({
        rule: 'Missing Patient Information',
        reason: `${field.label} is missing`
      });
    }
  }

  // Rule 2: Check for missing Peak HR, 1 min HRR, 2 min HRR
  const requiredHRFields = [
    { key: 'heart_rate_max', label: 'Peak HR' }
  ];

  for (const field of requiredHRFields) {
    if (!formData[field.key] || formData[field.key] === '' || formData[field.key] === null) {
      violations.push({
        rule: 'Missing Heart Rate Data',
        reason: `${field.label} is not available`
      });
    }
  }

  // Check for 1 min and 2 min HRR in training_zones
  let trainingZones = null;
  try {
    trainingZones = typeof formData.training_zones === 'string' 
      ? JSON.parse(formData.training_zones) 
      : formData.training_zones;
  } catch (e) {
    // If parsing fails, skip zone validation
    trainingZones = null;
  }

  if (trainingZones) {
    const recovery1min = trainingZones.recovery_1min;
    const recovery2min = trainingZones.recovery_2min;

    if (!recovery1min || recovery1min === '' || recovery1min === null) {
      violations.push({
        rule: 'Missing Heart Rate Data',
        reason: '1 min HRR (Heart Rate Recovery) is not available'
      });
    }

    if (!recovery2min || recovery2min === '' || recovery2min === null) {
      violations.push({
        rule: 'Missing Heart Rate Data',
        reason: '2 min HRR (Heart Rate Recovery) is not available'
      });
    }
  }

  // Rule 3: Check if Zone 1, 2, 3, 4 HR is 0
  if (trainingZones) {
    const zoneChecks = [
      { key: 'zone1_bpm', label: 'Zone 1' },
      { key: 'zone2_bpm', label: 'Zone 2' },
      { key: 'zone3_bpm', label: 'Zone 3' },
      { key: 'zone4_bpm', label: 'Zone 4' }
    ];

    for (const zone of zoneChecks) {
      const zoneValue = trainingZones[zone.key];
      // Check if zone value is 0 or contains 0 (for range values like "0-100")
      if (zoneValue === 0 || zoneValue === '0' || (typeof zoneValue === 'string' && zoneValue.startsWith('0'))) {
        violations.push({
          rule: 'Invalid Zone HR',
          reason: `${zone.label} HR cannot be 0`
        });
      }
    }
  }

  // Rule 4: Check for NaN:NaN in stage times (Page 4 - vo2_data_table)
  if (formData.vo2_data_table) {
    const vo2DataTable = typeof formData.vo2_data_table === 'string'
      ? JSON.parse(formData.vo2_data_table)
      : formData.vo2_data_table;

    if (Array.isArray(vo2DataTable)) {
      const invalidStages = [];
      vo2DataTable.forEach((stage, idx) => {
        if (stage.time === 'NaN:NaN' || !stage.time || stage.time === '') {
          invalidStages.push(`Stage ${stage.stage || idx + 1}`);
        }
      });

      if (invalidStages.length > 0) {
        violations.push({
          rule: 'Invalid Stage Data',
          reason: `${invalidStages.join(', ')} - time is NaN:NaN or missing`
        });
      }
    }
  }

  // Rule 5: Check if anaerobic threshold is < lower range of Zone 3
  if (formData.anaerobic_threshold && trainingZones) {
    const anaerobicThreshold = parseFloat(formData.anaerobic_threshold);
    const zone3Bpm = trainingZones.zone3_bpm;

    if (!isNaN(anaerobicThreshold) && zone3Bpm) {
      // Extract lower range from zone3_bpm (e.g., "157-169" -> 157)
      const zone3Lower = extractLowerRange(zone3Bpm);

      if (zone3Lower !== null && anaerobicThreshold < zone3Lower) {
        violations.push({
          rule: 'Anaerobic Threshold Out of Range',
          reason: `Anaerobic threshold ${anaerobicThreshold} is below Zone 3 lower range (${zone3Lower})`
        });
      }
    }
  }

  return {
    isValid: violations.length === 0,
    violations
  };
};

/**
 * Extract lower range value from zone string (e.g., "157-169" -> 157)
 */
const extractLowerRange = (zoneValue) => {
  if (!zoneValue) return null;

  const zoneStr = String(zoneValue).trim();
  
  // Skip if value is just dashes or empty
  if (zoneStr === '--' || zoneStr === '-' || zoneStr === '') return null;
  
  // Handle range format like "157-169"
  if (zoneStr.includes('-')) {
    const parts = zoneStr.split('-');
    // Get the first part and trim it
    const firstPart = parts[0].trim();
    
    // Skip if first part is empty
    if (!firstPart) return null;
    
    const lowerValue = parseFloat(firstPart);
    return isNaN(lowerValue) ? null : lowerValue;
  }

  // Handle single value
  const singleValue = parseFloat(zoneStr);
  return isNaN(singleValue) ? null : singleValue;
};
